import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Radio, 
  Wifi, 
  Video, 
  FileText, 
  Users, 
  Globe, 
  Palette, 
  Check, 
  Star,
  ArrowRight,
  Facebook,
  Mail,
  Phone
} from 'lucide-react';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });
  const [showForm, setShowForm] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend validation
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }
    
    // Show success message
    alert('¡Gracias por tu interés! Nos pondremos en contacto contigo pronto.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      message: ''
    });
    setShowForm(false);
  };

  const scrollToContact = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold text-blue-900">Xpresity</div>
          <nav className="hidden md:flex gap-6">
            <a href="#servicios" className="text-gray-700 hover:text-blue-600 transition-colors">Servicios</a>
            <a href="#planes" className="text-gray-700 hover:text-blue-600 transition-colors">Planes</a>
            <a href="#testimonios" className="text-gray-700 hover:text-blue-600 transition-colors">Testimonios</a>
          </nav>
          <Button onClick={scrollToContact} className="bg-blue-600 hover:bg-blue-700">
            Contacto
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Gestionamos tu Ecosistema<br />Digital Completo
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto">
              Tecnología, transmisión y automatización para organizaciones que quieren crecer
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={scrollToContact}
                className="bg-blue-500 hover:bg-blue-600 text-white text-lg px-8 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Solicitar Diagnóstico Gratuito
                <ArrowRight className="ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-6 rounded-lg backdrop-blur-sm transition-all duration-300 hover:scale-105"
              >
                Conoce Nuestros Servicios
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">Sobre Xpresity</h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Xpresity es un <span className="font-semibold text-blue-600">socio estratégico</span> que centraliza y gestiona 
            todo el ecosistema digital de organizaciones religiosas y educativas, permitiéndoles enfocarse en su misión 
            mientras nosotros optimizamos su tecnología.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 text-center mb-16">
            Servicios Principales
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Service 1 */}
            <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Radio className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-900">Infraestructura Tecnológica</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Sistemas de sonido profesional</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Streaming en vivo</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Redes de cámaras</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Configuración de transmisión online</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Service 2 */}
            <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-900">Digitalización de Procesos</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Contabilidad digital</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Control de membresía</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Automatización administrativa</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Centralización de información</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Service 3 */}
            <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-900">Plataforma Web Integrada</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Desarrollo web institucional</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Integración con redes sociales</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Clases y contenidos online</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Gestión de contenidos</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Service 4 */}
            <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Palette className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-900">Marca e Identidad Digital</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Modernización visual</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Estrategia digital</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Presencia online profesional</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Nos convertimos en tu departamento digital externo
          </h2>
          <p className="text-2xl md:text-3xl text-blue-100 font-light">
            Estabilidad. Profesionalización. Crecimiento sostenible.
          </p>
        </div>
      </section>

      {/* Plans Section */}
      <section id="planes" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 text-center mb-16">
            Planes de Servicio
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Plan 1 - Esencial */}
            <Card className="border-2 border-gray-300 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 flex flex-col">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl text-blue-900 mb-2">Esencial</CardTitle>
                <CardDescription className="text-base">Ideal para iniciar tu transformación digital</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Consultoría inicial</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Streaming básico</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Configuración de audio</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Presencia web básica</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
                  onClick={scrollToContact}
                >
                  Agendar Reunión
                </Button>
              </CardFooter>
            </Card>

            {/* Plan 2 - Gestión Digital */}
            <Card className="border-2 border-blue-500 hover:shadow-2xl transition-all duration-300 relative flex flex-col scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Más Popular
              </div>
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl text-blue-900 mb-2">Gestión Digital</CardTitle>
                <CardDescription className="text-base">Solución completa para organizaciones en crecimiento</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Todo lo del plan Esencial</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Digitalización completa</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Control de membresía</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Plataforma web profesional</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Soporte prioritario</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
                  onClick={scrollToContact}
                >
                  Agendar Reunión
                </Button>
              </CardFooter>
            </Card>

            {/* Plan 3 - Ecosistema Integral */}
            <Card className="border-2 border-gray-300 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 flex flex-col">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl text-blue-900 mb-2">Ecosistema Integral</CardTitle>
                <CardDescription className="text-base">Gestión completa de tu ecosistema digital</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Todo lo del plan Gestión Digital</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Infraestructura tecnológica avanzada</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Automatización completa</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Estrategia de marca</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Departamento digital dedicado</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
                  onClick={scrollToContact}
                >
                  Agendar Reunión
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonios" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 text-center mb-16">
            Lo Que Dicen Nuestros Clientes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Xpresity transformó completamente nuestra presencia digital. Ahora podemos enfocarnos en nuestra misión mientras ellos manejan toda la tecnología."
                </p>
                <p className="font-semibold text-blue-900">Pastor Juan Martínez</p>
                <p className="text-sm text-gray-600">Iglesia Comunidad de Fe</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "El streaming profesional y la automatización de procesos han permitido que nuestra institución crezca sin límites. Excelente servicio."
                </p>
                <p className="font-semibold text-blue-900">Dra. María González</p>
                <p className="text-sm text-gray-600">Directora Instituto Educativo</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Profesionalismo, compromiso y resultados reales. Son verdaderamente nuestro departamento digital externo."
                </p>
                <p className="font-semibold text-blue-900">Roberto Sánchez</p>
                <p className="text-sm text-gray-600">Director ONG Esperanza</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      {showForm && (
        <section id="contact-form" className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-2xl">
            <Card className="border-2 border-blue-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-blue-900 text-center">Solicita tu Diagnóstico Gratuito</CardTitle>
                <CardDescription className="text-center text-base">
                  Completa el formulario y nos pondremos en contacto contigo pronto
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nombre Completo *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Tu nombre"
                      required
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      required
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Tu teléfono"
                      required
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="organization">Organización</Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="Nombre de tu organización"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Mensaje</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Cuéntanos sobre tus necesidades..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6">
                    Enviar Solicitud
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold mb-8">
            Es momento de digitalizar tu misión
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Únete a las organizaciones que ya están transformando su futuro digital
          </p>
          <Button 
            size="lg"
            onClick={scrollToContact}
            className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-10 py-7 rounded-lg shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
          >
            Solicita tu Diagnóstico Ahora
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">Xpresity</h3>
              <p className="text-blue-200">
                Tu socio estratégico en transformación digital
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <div className="space-y-3">
                <a href="mailto:XpresityLlc@gmail.com" className="flex items-center text-blue-200 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 mr-2" />
                  XpresityLlc@gmail.com
                </a>
                <a href="tel:631-796-5294" className="flex items-center text-blue-200 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 mr-2" />
                  631-796-5294
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Síguenos</h4>
              <a 
                href="https://www.facebook.com/xpresity" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-blue-200 hover:text-white transition-colors"
              >
                <Facebook className="w-5 h-5 mr-2" />
                Facebook
              </a>
            </div>
          </div>
          
          <div className="border-t border-blue-800 pt-8 text-center text-blue-300">
            <p>&copy; {new Date().getFullYear()} Xpresity. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
